import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, MessageCircle, Phone, Clock, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';
import TextLink from './TextLink';

// Get context-aware content based on current page
const getPopupContent = (pathname) => {
  if (pathname.includes('residential-painting')) {
    return {
      title: "Need a Painting Quote?",
      question: "What type of painting project?",
      options: ["Interior", "Exterior", "Cabinets", "Full repaint"],
      icon: "🎨"
    };
  }
  if (pathname.includes('remodeling')) {
    return {
      title: "Planning a Remodel?",
      question: "Which area to renovate?",
      options: ["Kitchen", "Bathroom", "Multiple rooms", "Whole home"],
      icon: "🏠"
    };
  }
  if (pathname.includes('drywall')) {
    return {
      title: "Drywall Issues?",
      question: "What type of work?",
      options: ["Hole repair", "Water damage", "Texture match", "New install"],
      icon: "🔨"
    };
  }
  if (pathname.includes('carpentry')) {
    return {
      title: "Carpentry Needs?",
      question: "What work interests you?",
      options: ["Trim/molding", "Shelving", "Doors", "Custom work"],
      icon: "🪚"
    };
  }
  if (pathname.includes('tile')) {
    return {
      title: "Considering Tile?",
      question: "Where for tile?",
      options: ["Backsplash", "Bathroom", "Shower", "Floors"],
      icon: "🔲"
    };
  }
  if (pathname.includes('siding')) {
    return {
      title: "Siding Project?",
      question: "What brings you here?",
      options: ["Replacement", "Repair", "Storm damage", "New look"],
      icon: "🏡"
    };
  }
  if (pathname.includes('deck')) {
    return {
      title: "Deck Project?",
      question: "What's your goal?",
      options: ["New build", "Repair", "Staining", "Add cover"],
      icon: "🌳"
    };
  }
  if (pathname.includes('mold')) {
    return {
      title: "Mold Concerns?",
      question: "What's the situation?",
      options: ["Visible mold", "Musty smell", "Water damage", "Inspection"],
      icon: "🔬"
    };
  }
  if (pathname.includes('parking') || pathname.includes('striping')) {
    return {
      title: "Parking Lot Work?",
      question: "What do you need?",
      options: ["Striping", "ADA compliance", "Seal coat", "Full reno"],
      icon: "🅿️"
    };
  }
  if (pathname.includes('brick') || pathname.includes('masonry')) {
    return {
      title: "Masonry Work?",
      question: "What service?",
      options: ["Brick repair", "Tuckpointing", "Fireplace", "Stone work"],
      icon: "🧱"
    };
  }
  if (pathname.includes('roof')) {
    return {
      title: "Roof Issues?",
      question: "What's happening?",
      options: ["Active leak", "Missing shingles", "Storm damage", "Inspection"],
      icon: "🏠"
    };
  }
  // Default for homepage or other pages
  return {
    title: "Before You Go...",
    question: "What project interests you?",
    options: ["Painting", "Remodeling", "Repairs", "Not sure"],
    icon: "👋"
  };
};

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState('');
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  const content = getPopupContent(location.pathname);

  useEffect(() => {
    const popupShown = sessionStorage.getItem('exitPopupShown');
    if (popupShown) {
      setHasShown(true);
      return;
    }

    let timeOnSite = 0;
    const timeInterval = setInterval(() => { timeOnSite++; }, 1000);

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown && timeOnSite >= 20) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    const timer = setTimeout(() => {
      if (!hasShown && !sessionStorage.getItem('exitPopupShown')) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    }, 60000);

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
      clearInterval(timeInterval);
    };
  }, [hasShown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: '',
          service: selectedOption || 'general-inquiry',
          city: '',
          details: `[Exit Popup] Interest: ${selectedOption}`,
        })
      });

      if (response.ok) {
        toast.success("Thanks! We'll reach out shortly.");
        setIsVisible(false);
      } else {
        throw new Error('Failed');
      }
    } catch {
      toast.error('Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsVisible(false)} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        <button 
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 z-10"
          data-testid="exit-popup-close"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-6 text-center">
          <div className="text-4xl mb-2">{content.icon}</div>
          <h2 className="text-xl font-bold text-white">{content.title}</h2>
        </div>

        <div className="p-6">
          {step === 1 ? (
            <>
              <p className="text-neutral-700 font-medium text-center mb-4">{content.question}</p>
              <div className="grid grid-cols-2 gap-3">
                {content.options.map((option) => (
                  <button
                    key={option}
                    onClick={() => { setSelectedOption(option); setStep(2); }}
                    className="p-3 border-2 border-neutral-200 rounded-lg text-sm font-medium text-neutral-700 hover:border-amber-500 hover:bg-amber-50 transition-all"
                  >
                    {option}
                  </button>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-neutral-200">
                <p className="text-xs text-neutral-500 text-center mb-3">Or contact us:</p>
                <div className="flex gap-2">
                  <a href={`tel:${companyInfo.phone}`} className="flex-1">
                    <Button variant="outline" className="w-full text-sm">
                      <Phone className="w-4 h-4 mr-1" /> Call
                    </Button>
                  </a>
                  <TextLink className="flex-1">
                    <Button variant="outline" className="w-full text-sm">
                      <MessageCircle className="w-4 h-4 mr-1" /> Text
                    </Button>
                  </TextLink>
                </div>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">{selectedOption}</span>
                </div>
              </div>

              <div>
                <Label htmlFor="popupName" className="text-sm">Your Name</Label>
                <Input 
                  id="popupName"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="First name"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="popupPhone" className="text-sm">Phone Number</Label>
                <Input 
                  id="popupPhone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(214) 555-1234"
                  required
                  className="mt-1"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Get My Free Quote'}
              </Button>

              <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-neutral-500">
                ← Back
              </button>

              <div className="flex items-center justify-center gap-4 text-xs text-neutral-400">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Quick response</span>
                <span>•</span>
                <span>No obligation</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
